import {
  LottieAnimations,
  LottieView,
  MFAChannel,
} from '@eCommerce/frontend-legacy-shared-library';
import {supportPhoneNumber} from 'models/supportPhoneNumber';
import {Link, Pressable, Text, View} from 'native-base';
import {ROUTES} from 'routes';

import BodyText from 'components/PanelContainer/BodyText';
import ReactInputVerificationCode from 'components/ReactInputVerificationCode';
import {useIsMD} from 'hooks/useIsMD';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';

const RESEND_EMAIL = 'Resend email';
const RESEND_SMS = 'Resend SMS';

export interface VerificationCodeInputProps {
  isCodeInvalid: boolean;
  onResendCode?: () => void;
  isLoading: boolean;
  channel: MFAChannel | undefined;
  onCodeEnter: (code: string) => void;
  isVip: boolean;
  isOverMaxCodeAttempts: boolean;
}

const VerificationCodeInput: React.FunctionComponent<
  VerificationCodeInputProps
> = (props) => {
  const {
    isCodeInvalid,
    onResendCode,
    isLoading = false,
    channel,
    onCodeEnter,
    isVip,
    isOverMaxCodeAttempts,
  } = props;

  const navigate = useNavigateWithParams();
  const isMD = useIsMD();
  const getSupportNumber = () => {
    const contactHostText = 'Contact your Host.';
    const contactUsText = 'Call ';

    return (
      <View flexDirection="row">
        <Text color="tuna" fontSize="12px" fontFamily="body">
          Need help? {isVip ? null : contactUsText}
        </Text>
        <Link
          _hover={{
            textDecoration: 'underline',
          }}
          onPress={() => isVip && navigate(ROUTES.hostInfo)}
          href={!isVip ? `tel:${supportPhoneNumber}` : undefined}
        >
          <Text color="tuna" fontSize="12px" fontFamily="body" bold>
            {isVip ? contactHostText : supportPhoneNumber}
          </Text>
        </Link>
      </View>
    );
  };

  return (
    <View
      w="100%"
      minHeight="248px"
      alignItems="center"
      bgColor="ghostWhite"
      py={isMD ? '32px' : '24px'}
      px="16px"
      borderRadius="8px"
    >
      <Text color="tuna" mb="24px" fontFamily="body" fontWeight="bold">
        Please enter the 6-digit code:
      </Text>
      <View
        alignItems="center"
        minWidth={isMD ? '328px' : undefined}
        w="100%"
        mb="24px"
        maxW="328px"
      >
        <View
          w="100%"
          justifyContent="space-between"
          alignItems="center"
          flexDirection="row"
        >
          {isLoading ? (
            <View w="100%">
              <LottieView
                source={LottieAnimations.loaderAnimation}
                style={{height: 64, width: 64}}
                loop={true}
                autoPlay={true}
              />
            </View>
          ) : (
            <ReactInputVerificationCode
              onCompleted={onCodeEnter}
              placeholder=""
              length={6}
              isInvalid={isCodeInvalid}
              isOverMaxCodeAttempts={isOverMaxCodeAttempts}
              ariaLabelPrefix="verification"
            />
          )}
        </View>
        {isCodeInvalid && !isOverMaxCodeAttempts && (
          <Text
            alignSelf="flex-start"
            color="#D60000"
            fontSize="11px"
            fontFamily="body"
          >
            Invalid code entered, please try again.
          </Text>
        )}
      </View>
      <View
        flexDirection={isOverMaxCodeAttempts ? 'column' : 'row'}
        justifyContent="center"
        alignItems="center"
      >
        {isOverMaxCodeAttempts ? (
          <BodyText fontSize="12px" color="#FF0000">
            You have reached the maximum number of attempts.{' '}
          </BodyText>
        ) : (
          <BodyText fontSize="12px">Didn&apos;t receive a code? </BodyText>
        )}
        <Pressable
          onPress={onResendCode}
          _hover={{textDecoration: 'underline'}}
        >
          <BodyText fontSize="12px" bold>
            {!channel
              ? 'Resend'
              : channel === MFAChannel.EMAIL
              ? RESEND_EMAIL
              : RESEND_SMS}
          </BodyText>
        </Pressable>
      </View>
      {getSupportNumber()}
    </View>
  );
};

export default VerificationCodeInput;
